import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, IconButton, Avatar, Chip, Tooltip } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useMultiFetchQueue } from '../hooks/useFetchQueue';
import { requestGetPendingTimeOff, requestGetCurrentlyOut } from '../redux/sagas/requests/timeoff';
import { reviewTimeOff } from '../redux/ducks/timeoff';
import { showAlert } from '../redux/slices/alerts';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { formatMonthDay } from '../utils/dateUtils';

const Section = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
}));

const PendingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
}));

const PendingMeta = styled(Box)({
    flex: 1,
    minWidth: 0,
});

const PendingName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const PendingDetails = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const Actions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(0.5),
}));

const SimpleRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
}));

const RowLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
    flex: 1,
}));

const RowDates = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const ScrollList = styled(Box)({
    maxHeight: 220,
    overflowY: 'auto',
});

const EmptyText = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.smd,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(1, 0),
}));

const TypeChip = styled(Chip)(({ theme }) => ({
    height: 20,
    fontSize: theme.typography.size.xs,
}));

const PendingAvatar = styled(Avatar)({
    width: 32,
    height: 32,
});

const SmallAvatar = styled(Avatar)({
    width: 24,
    height: 24,
});

const formatDate = (d) => formatMonthDay(d);

const formatRange = (start, end) => {
    const s = formatDate(start);
    const e = formatDate(end);
    if (!s) return e;
    if (!e || s === e) return s;
    return `${s} – ${e}`;
};

const fullName = (user) => {
    if (!user) return 'Unknown';
    if (user.fullName) return user.fullName;
    return [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Unknown';
};

const avatarSrc = (user) => user?.photo || undefined;

const TimeOffApprovalWidget = ({ widgetId, onRemove }) => {
    const dispatch = useDispatch();

    const { results, refresh, isLoading, error } = useMultiFetchQueue(widgetId, [
        { apiCall: requestGetPendingTimeOff, cacheKey: `timeoff-pending` },
        { apiCall: requestGetCurrentlyOut, cacheKey: `timeoff-currently-out` },
    ]);

    const pending = results?.[0]?.data?.pending || [];
    const out = results?.[1]?.data || { current: [], upcoming: [] };
    const upcoming = out.upcoming || [];
    const upcomingTitle = out.upcomingDays ? `Upcoming (${upcoming.length}) · Next ${out.upcomingDays} Days` : `Upcoming (${upcoming.length})`;

    const handleReview = (id, status) => {
        dispatch(reviewTimeOff(id, status, (response) => {
            const failed = !response || response?.error;
            if (failed) {
                dispatch(showAlert({ severity: 'error', message: response?.error || 'Could not update request' }));
                return;
            }
            dispatch(showAlert({
                severity: 'success',
                message: status === 'Approved' ? 'Time off approved' : 'Time off rejected',
            }));
            dispatch(triggerRefresh({ triggerName: 'timeOff' }));
            refresh();
        }));
    };

    return (
        <BaseWidget
            title="Time Off Approvals"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            <Section>
                <SectionTitle>Pending Requests ({pending.length})</SectionTitle>
                {pending.length === 0 ? (
                    <EmptyText>No pending requests</EmptyText>
                ) : pending.map((entry) => (
                    <PendingRow key={entry._id}>
                        <PendingAvatar src={avatarSrc(entry.user)} alt={fullName(entry.user)}>
                            {fullName(entry.user).charAt(0)}
                        </PendingAvatar>
                        <PendingMeta>
                            <PendingName>{fullName(entry.user)}</PendingName>
                            <PendingDetails>
                                <TypeChip label={entry.type} size="small" /> {formatRange(entry.startDate, entry.endDate)} · {entry.days || 0}d
                                {entry.description ? ` · ${entry.description}` : ''}
                            </PendingDetails>
                        </PendingMeta>
                        <Actions>
                            <Tooltip title="Approve">
                                <IconButton size="small" color="success" onClick={() => handleReview(entry._id, 'Approved')}>
                                    <CheckCircle fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                                <IconButton size="small" color="error" onClick={() => handleReview(entry._id, 'Rejected')}>
                                    <Cancel fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Actions>
                    </PendingRow>
                ))}
            </Section>

            <Section>
                <SectionTitle>Out Today ({out.current?.length || 0})</SectionTitle>
                {(!out.current || out.current.length === 0) ? (
                    <EmptyText>Nobody is out today</EmptyText>
                ) : out.current.map((entry) => (
                    <SimpleRow key={entry._id}>
                        <SmallAvatar src={avatarSrc(entry.user)} alt={fullName(entry.user)}>
                            {fullName(entry.user).charAt(0)}
                        </SmallAvatar>
                        <RowLabel>{fullName(entry.user)}</RowLabel>
                        <RowDates>{formatRange(entry.startDate, entry.endDate)}</RowDates>
                    </SimpleRow>
                ))}
            </Section>

            <Section>
                <SectionTitle>{upcomingTitle}</SectionTitle>
                {upcoming.length === 0 ? (
                    <EmptyText>No upcoming time off</EmptyText>
                ) : (
                    <ScrollList>
                        {upcoming.map((entry) => (
                            <SimpleRow key={entry._id}>
                                <SmallAvatar src={avatarSrc(entry.user)} alt={fullName(entry.user)}>
                                    {fullName(entry.user).charAt(0)}
                                </SmallAvatar>
                                <RowLabel>{fullName(entry.user)}</RowLabel>
                                <RowDates>{formatRange(entry.startDate, entry.endDate)}</RowDates>
                            </SimpleRow>
                        ))}
                    </ScrollList>
                )}
            </Section>
        </BaseWidget>
    );
};

export default TimeOffApprovalWidget;
